import * as Sentry from '@sentry/vue'
import type { Translations } from '@layers/app/stores/types/locale.ts'

import type {
  FetchRuntimeTranslationsParams,
} from '@layers/web/services/types/locale-api.ts'
import { applySiteVariantToTranslations } from '@layers/web/utils/site-variant.ts'

/**
 * Locale API service for fetching translation data
 */
export class LocaleApiService {
  /**
   * Fetch locale translations from API during runtime
   * Uses apiFetch for proper Nuxt integration with error handling
   * @param params - Runtime fetch parameters
   * @returns Translation object with countryCodeList and translation keys
   */
  static async fetchRuntimeTranslations ({ cacheReload = false }: FetchRuntimeTranslationsParams = {}): Promise<Translations | null> {
    const { market, customerServicePhone, dateFormatPlaceholder } = useRuntimeConfig().public
    try {
      const url = `/${market}/locale${cacheReload ? '?cache_reload=1' : ''}`
      const { data: result } = await apiFetch<Translations>(url, { simple: true })

      return result
        ? applySiteVariantToTranslations(result, { customerServicePhone, dateFormatPlaceholder })
        : result
    } catch (error: any) {
      Sentry.setContext('details', {
        market,
        error: error.data,
      })

      Sentry.captureException(new Error('Failed to fetch locale translations at runtime'), {
        level: 'error',
        tags: {
          type: 'LOCALE-API',
          operation: 'fetchRuntimeTranslations',
          service: 'locale-api',
          market,
        },
      })

      return null
    }
  }

  static async fetchLocalTranslations (pageName = 'general'): Promise<Translations> {
    const { market } = useRuntimeConfig().public

    const data = await $fetch(`/_locales/${pageName}/${market}/data.json`)
    return data
  }
}
